// src/app/slice/privateAlbumSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import privateAlbumService from "../../features/privateAlbum/service/privateAlbumService";
import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
} from "../../features/privateAlbum/types/privateAlbumTypes";

// ============ ASYNC THUNKS ============

// Album CRUD
export const fetchAlbums = createAsyncThunk(
  "privateAlbum/fetchAlbums",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await privateAlbumService.getAlbums(filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

export const fetchAlbumById = createAsyncThunk(
  "privateAlbum/fetchAlbumById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await privateAlbumService.getAlbumById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_ONE_FAILED);
    }
  },
);

export const createAlbum = createAsyncThunk(
  "privateAlbum/createAlbum",
  async (data, { rejectWithValue }) => {
    try {
      const response = await privateAlbumService.createAlbum(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.CREATE_FAILED);
    }
  },
);

export const updateAlbum = createAsyncThunk(
  "privateAlbum/updateAlbum",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await privateAlbumService.updateAlbum(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

export const deleteAlbum = createAsyncThunk(
  "privateAlbum/deleteAlbum",
  async (id, { rejectWithValue }) => {
    try {
      await privateAlbumService.deleteAlbum(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.DELETE_FAILED);
    }
  },
);

export const archiveAlbum = createAsyncThunk(
  "privateAlbum/archiveAlbum",
  async (id, { rejectWithValue }) => {
    try {
      const response = await privateAlbumService.archiveAlbum(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

export const restoreAlbum = createAsyncThunk(
  "privateAlbum/restoreAlbum",
  async (id, { rejectWithValue }) => {
    try {
      const response = await privateAlbumService.restoreAlbum(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

// Photo Management
export const fetchAlbumPhotos = createAsyncThunk(
  "privateAlbum/fetchAlbumPhotos",
  async (albumId, { rejectWithValue }) => {
    try {
      const response = await privateAlbumService.getAlbumPhotos(albumId);
      return { albumId, photos: response };
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

export const uploadPhotos = createAsyncThunk(
  "privateAlbum/uploadPhotos",
  async ({ albumId, photosData, uploadedBy }, { rejectWithValue }) => {
    try {
      const response = await privateAlbumService.uploadPhotos(
        albumId,
        photosData,
        uploadedBy,
      );
      return { albumId, ...response };
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPLOAD_FAILED);
    }
  },
);

export const deletePhoto = createAsyncThunk(
  "privateAlbum/deletePhoto",
  async (photoId, { rejectWithValue }) => {
    try {
      await privateAlbumService.deletePhoto(photoId);
      return photoId;
    } catch (error) {
      return rejectWithValue(
        error.message || ERROR_MESSAGES.PHOTO_DELETE_FAILED,
      );
    }
  },
);

export const updatePhoto = createAsyncThunk(
  "privateAlbum/updatePhoto",
  async ({ photoId, data }, { rejectWithValue }) => {
    try {
      const response = await privateAlbumService.updatePhoto(photoId, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

export const setCoverPhoto = createAsyncThunk(
  "privateAlbum/setCoverPhoto",
  async ({ albumId, photoId }, { rejectWithValue }) => {
    try {
      await privateAlbumService.setCoverPhoto(albumId, photoId);
      return { albumId, photoId };
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

// Access Management
export const shareAlbum = createAsyncThunk(
  "privateAlbum/shareAlbum",
  async ({ albumId, email, name, role, invitedBy }, { rejectWithValue }) => {
    try {
      const response = await privateAlbumService.shareAlbum(
        albumId,
        email,
        name,
        role,
        invitedBy,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.SHARE_FAILED);
    }
  },
);

export const revokeAccess = createAsyncThunk(
  "privateAlbum/revokeAccess",
  async (accessId, { rejectWithValue }) => {
    try {
      await privateAlbumService.revokeAccess(accessId);
      return accessId;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.REVOKE_FAILED);
    }
  },
);

export const updateAccessRole = createAsyncThunk(
  "privateAlbum/updateAccessRole",
  async ({ accessId, role }, { rejectWithValue }) => {
    try {
      const response = await privateAlbumService.updateAccessRole(
        accessId,
        role,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

// Statistics
export const fetchAlbumStats = createAsyncThunk(
  "privateAlbum/fetchAlbumStats",
  async (albumId, { rejectWithValue }) => {
    try {
      const response = await privateAlbumService.getAlbumStats(albumId);
      return { albumId, stats: response };
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      // Return empty stats instead of rejecting
      return {
        albumId,
        stats: {
          totalViews: 0,
          totalDownloads: 0,
          totalComments: 0,
          uniqueViewers: 0,
          uniqueDownloaders: 0,
          mostViewedPhotos: [],
          accessList: [],
          totalAccess: 0,
          activeAccess: 0,
          pendingAccess: 0,
        },
      };
    }
  },
);

export const fetchOverallStats = createAsyncThunk(
  "privateAlbum/fetchOverallStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await privateAlbumService.getOverallStats();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

// ============ INITIAL STATE ============

const initialState = {
  albums: [],
  selectedAlbum: null,
  photos: [],
  accessList: [],
  logs: [],
  stats: {
    totalAlbums: 0,
    activeAlbums: 0,
    archivedAlbums: 0,
    totalPhotos: 0,
    totalSize: 0,
    totalViews: 0,
    averagePhotosPerAlbum: 0,
  },
  loading: false,
  error: null,
  success: null,
  filters: {
    status: "all",
    eventType: "all",
    search: "",
    clientEmail: "",
  },
  sortBy: "createdAt",
  sortOrder: "desc",
};

// ============ SLICE ============

const privateAlbumSlice = createSlice({
  name: "privateAlbum",
  initialState,
  reducers: {
    setAlbums: (state, action) => {
      state.albums = action.payload;
    },
    setSelectedAlbum: (state, action) => {
      state.selectedAlbum = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setSort: (state, action) => {
      state.sortBy = action.payload.sortBy || state.sortBy;
      state.sortOrder = action.payload.sortOrder || state.sortOrder;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    resetState: (state) => {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      // ============ FETCH ALBUMS ============
      .addCase(fetchAlbums.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAlbums.fulfilled, (state, action) => {
        state.loading = false;
        state.albums = action.payload;
      })
      .addCase(fetchAlbums.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ============ FETCH ALBUM BY ID ============
      .addCase(fetchAlbumById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAlbumById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAlbum = action.payload;
        state.photos = action.payload.photos || [];
        state.accessList = action.payload.accessList || [];
      })
      .addCase(fetchAlbumById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ============ CREATE ALBUM ============
      .addCase(createAlbum.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createAlbum.fulfilled, (state, action) => {
        state.loading = false;
        state.albums.unshift(action.payload);
        state.selectedAlbum = action.payload;
        state.success = SUCCESS_MESSAGES.CREATE_SUCCESS;
      })
      .addCase(createAlbum.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ============ UPDATE ALBUM ============
      .addCase(updateAlbum.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateAlbum.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.albums.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.albums[index] = action.payload;
        }
        if (state.selectedAlbum?.id === action.payload.id) {
          state.selectedAlbum = action.payload;
        }
        state.success = SUCCESS_MESSAGES.UPDATE_SUCCESS;
      })
      .addCase(updateAlbum.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ============ DELETE ALBUM ============
      .addCase(deleteAlbum.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteAlbum.fulfilled, (state, action) => {
        state.loading = false;
        state.albums = state.albums.filter((a) => a.id !== action.payload);
        if (state.selectedAlbum?.id === action.payload) {
          state.selectedAlbum = null;
          state.photos = [];
          state.accessList = [];
        }
        state.success = SUCCESS_MESSAGES.DELETE_SUCCESS;
      })
      .addCase(deleteAlbum.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ============ ARCHIVE ALBUM ============
      .addCase(archiveAlbum.fulfilled, (state, action) => {
        const index = state.albums.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.albums[index] = action.payload;
        }
        if (state.selectedAlbum?.id === action.payload.id) {
          state.selectedAlbum = action.payload;
        }
      })
      .addCase(restoreAlbum.fulfilled, (state, action) => {
        const index = state.albums.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.albums[index] = action.payload;
        }
        if (state.selectedAlbum?.id === action.payload.id) {
          state.selectedAlbum = action.payload;
        }
      })

      // ============ FETCH ALBUM PHOTOS ============
      .addCase(fetchAlbumPhotos.fulfilled, (state, action) => {
        state.photos = action.payload.photos;
      })

      // ============ UPLOAD PHOTOS ============
      .addCase(uploadPhotos.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(uploadPhotos.fulfilled, (state, action) => {
        state.loading = false;
        // Refresh album data
        if (state.selectedAlbum?.id === action.payload.albumId) {
          state.selectedAlbum.totalPhotos = action.payload.totalPhotos;
          state.selectedAlbum.totalSize = action.payload.totalSize;
        }
        state.success = SUCCESS_MESSAGES.UPLOAD_SUCCESS;
      })
      .addCase(uploadPhotos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ============ DELETE PHOTO ============
      .addCase(deletePhoto.fulfilled, (state, action) => {
        state.photos = state.photos.filter((p) => p.id !== action.payload);
        if (state.selectedAlbum) {
          state.selectedAlbum.totalPhotos = Math.max(
            0,
            (state.selectedAlbum.totalPhotos || 0) - 1,
          );
        }
      })

      // ============ UPDATE PHOTO ============
      .addCase(updatePhoto.fulfilled, (state, action) => {
        const index = state.photos.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.photos[index] = action.payload;
        }
      })

      // ============ SET COVER PHOTO ============
      .addCase(setCoverPhoto.fulfilled, (state, action) => {
        state.photos.forEach((p) => {
          p.isCover = p.id === action.payload.photoId;
        });
        if (state.selectedAlbum?.id === action.payload.albumId) {
          const cover = state.photos.find(
            (p) => p.id === action.payload.photoId,
          );
          if (cover) {
            state.selectedAlbum.coverImageUrl = cover.url;
            state.selectedAlbum.coverThumbnailUrl =
              cover.thumbnailUrl || cover.url;
          }
        }
      })

      // ============ SHARE ALBUM ============
      .addCase(shareAlbum.fulfilled, (state, action) => {
        state.accessList.push(action.payload);
        if (state.selectedAlbum) {
          state.selectedAlbum.accessCount =
            (state.selectedAlbum.accessCount || 0) + 1;
        }
        state.success = SUCCESS_MESSAGES.SHARE_SUCCESS;
      })

      // ============ REVOKE ACCESS ============
      .addCase(revokeAccess.fulfilled, (state, action) => {
        state.accessList = state.accessList.filter(
          (a) => a.id !== action.payload,
        );
        if (state.selectedAlbum) {
          state.selectedAlbum.accessCount = Math.max(
            0,
            (state.selectedAlbum.accessCount || 0) - 1,
          );
        }
        state.success = SUCCESS_MESSAGES.ACCESS_REVOKED;
      })

      // ============ UPDATE ACCESS ROLE ============
      .addCase(updateAccessRole.fulfilled, (state, action) => {
        const index = state.accessList.findIndex(
          (a) => a.id === action.payload.id,
        );
        if (index !== -1) {
          state.accessList[index] = action.payload;
        }
      })

      // ============ FETCH ALBUM STATS ============
      .addCase(fetchAlbumStats.fulfilled, (state, action) => {
        // Stats are stored in the component state
      })

      // ============ FETCH OVERALL STATS ============
      .addCase(fetchOverallStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export const {
  setAlbums,
  setSelectedAlbum,
  setFilters,
  clearFilters,
  setSort,
  clearError,
  clearSuccess,
  resetState,
} = privateAlbumSlice.actions;

export default privateAlbumSlice.reducer;
